import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Sentry } from '../utils/sentry';

// Global error handler - will be initialized by App.tsx
let globalErrorHandler: ((error: any) => void) | null = null;

// Session expiry handler - will be initialized by App.tsx
let sessionExpiryHandler: (() => void) | null = null;

// API Error Modal handler - will be initialized by App.tsx
let apiErrorModalHandler: ((error: any) => void) | null = null;

// Workflow modal close handler - will be initialized by TenantDashboard
let workflowModalCloseHandler: (() => void) | null = null;

// Global agent name - will be set when workflow opens and cleared when it closes
let currentAgentName: string | null = null;

export const setGlobalErrorHandler = (handler: (error: any) => void) => {
  globalErrorHandler = handler;
};

export const setSessionExpiryHandler = (handler: () => void) => {
  sessionExpiryHandler = handler;
};

export const setApiErrorModalHandler = (handler: (error: any) => void) => {
  apiErrorModalHandler = handler;
};

export const setWorkflowModalCloseHandler = (handler: () => void) => {
  workflowModalCloseHandler = handler;
};

export const getWorkflowModalCloseHandler = () => {
  return workflowModalCloseHandler;
};

export const setCurrentAgentName = (agentName: string | null) => {
  currentAgentName = agentName;
  console.log('🤖 Current agent name set to:', agentName);
};

export const getCurrentAgentName = () => {
  return currentAgentName;
};

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
  requestId?: string;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: this.getBaseURL(),
      timeout: 90000,
      withCredentials: true, // Enable cookies
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': '2.0.0',
      },
    });

    this.setupInterceptors();
  }

  private getBaseURL(): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    console.log('🔗 API Base URL from env:', baseUrl);
    console.log('🔗 All Vite env vars:', import.meta.env);
    // Empty string means use relative URLs with Vite proxy in development
    return baseUrl || '';
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add request ID for tracing
        const requestId = uuidv4();
        config.headers['X-Request-Id'] = requestId;

        // Add tenant ID from current domain
        const tenantId = this.getTenantId();
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId;
        }
        // Add CSRF token from cookies for state-changing operations
        if (['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
          const csrfToken = this.getCsrfTokenFromCookie();

          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        // Add agent parameter to request body for POST/PUT/PATCH requests
        // Priority: 1) Use agent from request data if present, 2) Use global agent if set
        if (['POST', 'PUT', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
          if (config.data && typeof config.data === 'object') {
            // Only add global agent if:
            // 1. There's a global agent name set
            // 2. The request data doesn't already have an agent field
            if (currentAgentName && !config.data.agent) {
              config.data = {
                ...config.data,
                agent: currentAgentName
              };
            }
          }
        }

        console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          requestId,
          tenantId,
          hasCsrf: !!config.headers['X-CSRF-Token'],
          agent: currentAgentName || 'none'
        });

        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(this.createApiError(error));
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const requestId = response.config.headers['X-Request-Id'];

        console.log(`✅ API Response: ${response.status}`, {
          requestId,
          url: response.config.url,
        });

        // Extract and store CSRF token from response
        this.updateCsrfTokenFromResponse(response);

        // Check for session expiry in successful response body
        // Some APIs return 200 with { success: false, error: "Invalid session token" }
        if (response.data && !response.data.success) {
          const errorMessage = response.data.error || response.data.message || '';
          if (
            errorMessage.toLowerCase().includes('invalid session') ||
            errorMessage.toLowerCase().includes('session expired') ||
            errorMessage.toLowerCase().includes('session token')
          ) {
            console.warn('🔐 Session expired detected in response body - clearing data and triggering session expiry handler');

            // Clear cookies and localStorage
            this.clearAllAuthData();

            if (sessionExpiryHandler) {
              sessionExpiryHandler();
            }
          }
        }

        return response;
      },
      (error) => {
        const originalRequest = error.config;
        const requestId = originalRequest?.headers['X-Request-Id'];

        console.error(`❌ API Error: ${error.response?.status || 'Network'}`, {
          requestId,
          url: originalRequest?.url,
          message: error.response?.data?.error?.message || error.message,
        });

        // Create enhanced error 
        const enhancedError = this.createApiError(error);
        enhancedError.requestId = requestId;

        // Check for session expiry (401 Unauthorized)
        if (error.response?.status === 401) {
          console.warn('🔐 Session expired - clearing data and triggering session expiry handler');

          // Clear cookies and localStorage
          this.clearAllAuthData();

          if (sessionExpiryHandler) {
            sessionExpiryHandler();
            // Don't show regular error notification for session expiry
            return Promise.reject(enhancedError);
          }
        }

        // Check if this is a critical error that should show ErrorModal
        const shouldShowModal = this.shouldShowErrorModal(error);
        if (apiErrorModalHandler && shouldShowModal) {
          Sentry.captureException(error, {
            tags: { source: 'api', critical: 'true', status: String(error.response?.status || 'network') },
            extra: { requestId, url: originalRequest?.url, method: originalRequest?.method },
          });
          apiErrorModalHandler(enhancedError);
          // Don't also show toast notification
          return Promise.reject(enhancedError);
        }

        // Only show notifications for certain types of errors (not business logic errors)
        const shouldShowNotification = this.shouldShowErrorNotification(error);
        if (globalErrorHandler && shouldShowNotification) {
          globalErrorHandler(enhancedError);
        }

        return Promise.reject(enhancedError);
      }
    );
  }

  // Cookie utility methods
  private getCsrfTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie => cookie.trim().startsWith('csrf_token='));
    return csrfCookie ? csrfCookie.split('=')[1].trim() : null;
  }

  private getSessionTokenFromCookie(): string | null {
    const cookies = document.cookie.split(';');
    // Try multiple possible session cookie names
    const sessionCookieNames = ['session_token=', 'session=', 'sessionid=', 'connect.sid='];
    for (const cookieName of sessionCookieNames) {
      const sessionCookie = cookies.find(cookie => cookie.trim().startsWith(cookieName));
      if (sessionCookie) {
        return sessionCookie.split('=')[1].trim();
      }
    }
    
    console.warn('🍪 No session cookie found. Available cookies:', document.cookie);
    return null;
  }

  private setCookie(name: string, value: string, options: { httpOnly?: boolean; secure?: boolean; sameSite?: string; maxAge?: number } = {}): void {
    let cookieString = `${name}=${value}`;

    if (options.maxAge) {
      cookieString += `; Max-Age=${options.maxAge}`;
    }
    if (options.secure) {
      cookieString += '; Secure';
    }
    if (options.sameSite) {
      cookieString += `; SameSite=${options.sameSite}`;
    }
    cookieString += '; Path=/';
    const domain = import.meta.env.VITE_SESSION_COOKIE_DOMAIN as string | undefined;
    if (domain) cookieString += `; Domain=${domain}`;

    document.cookie = cookieString;
  }

  private clearCookie(name: string): void {
    const base = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    // Host-only delete — covers a cookie set with no Domain attribute.
    document.cookie = base;
    // session_token is set domain-scoped (Domain=.{registrable-domain}) so it is
    // shared across app.{property}.com and {property}.com. A delete only removes a
    // cookie whose Domain matches, and this shared multi-tenant build has no single
    // hardcoded domain (different hotels run on different domains). Issue the delete
    // at every parent scope of the current host; exactly one matches whatever the
    // backend set. The public-suffix scope (e.g. ".com") is rejected by the browser
    // and is harmless. Without this the stale cookie survives the clear and the app
    // loops on session-expired → reload. See sessionUtils.clearSessionCookie.
    const parts = window.location.hostname.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      document.cookie = `${base}; Domain=.${parts.slice(i).join('.')}`;
    }
    // Honor an explicit build-time override if one is configured.
    const domain = import.meta.env.VITE_SESSION_COOKIE_DOMAIN as string | undefined;
    if (domain) document.cookie = `${base}; Domain=${domain}`;
  }

  private updateCsrfTokenFromResponse(response: any): void {
    // Check multiple possible locations for CSRF token in response
    let newCsrfToken: string | null = null;

    // Method 1: Check response headers
    if (response.headers && response.headers['x-csrf-token']) {
      newCsrfToken = response.headers['x-csrf-token'];
    }
    // Method 2: Check response data (common API response format)
    else if (response.data?.csrfToken) {
      newCsrfToken = response.data.csrfToken;
    }

    // If we found a new CSRF token, store it in cookies
    if (newCsrfToken) {
      this.setCookie('csrf_token', newCsrfToken, {
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        maxAge: 60 * 60 * 24 // 24 hours
      });
      console.log('🔒 CSRF token updated from API response');
    }
  }

  private getTenantId(): string | null {
    const hostname = window.location.hostname;
    
    // Extract subdomain for production
    if (hostname.includes('.')) {
      const subdomain = hostname.split('.')[0];
      return subdomain !== 'www' && subdomain !== 'api' ? subdomain : null;
    }
    
    // Development fallback
    if (hostname === 'localhost') {
      const params = new URLSearchParams(window.location.search);
      return params.get('tenant') || 'demo';
    }
    
    return null;
  }

  private createApiError(error: any): any {
    // Preserve the original error object
    const enhancedError = { ...error };

    // Handle structured API error responses
    if (error.response?.data?.error) {
      enhancedError.message = error.response.data.error.message || error.response.data.message || 'API Error';
      enhancedError.code = error.response.data.error.code || `HTTP_${error.response.status}`;
      enhancedError.status = error.response.status;
      // Preserve header and detail fields for ErrorModal
      enhancedError.header = error.response.data.error.header;
      enhancedError.detail = error.response.data.error.detail;
      // Also preserve the entire error object for nested access
      enhancedError.error = error.response.data.error;
      return enhancedError;
    }

    // Handle direct API errors in data
    if (error.response?.data?.message) {
      enhancedError.message = error.response.data.message;
      enhancedError.code = `HTTP_${error.response.status}`;
      enhancedError.status = error.response.status;
      return enhancedError;
    }

    // Handle network errors
    if (!error.response) {
      enhancedError.code = 'NETWORK_ERROR';
      enhancedError.message = 'Network connection failed. Please check your internet connection.';
      enhancedError.status = 0;
      return enhancedError;
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      enhancedError.code = 'TIMEOUT_ERROR';
      enhancedError.message = 'Request timed out. Please try again.';
      enhancedError.status = 0;
      return enhancedError;
    }

    // Handle HTTP status codes
    const status = error.response?.status;
    const statusMessages: Record<number, string> = {
      400: 'Invalid request. Please check your input.',
      401: 'Authentication required. Please log in.',
      403: 'Insufficient permissions for this operation.',
      404: 'The requested resource was not found.',
      429: 'Too many requests. Please try again later.',
      500: 'Internal server error. Please try again.',
      503: 'Service temporarily unavailable. Please try again later.',
    };

    enhancedError.code = `HTTP_${status}`;
    enhancedError.message = statusMessages[status] || error.message || 'An unexpected error occurred';
    enhancedError.status = status || 500;
    
    return enhancedError;
  }

  private shouldShowErrorModal(error: any): boolean {
    const status = error.response?.status;
    const code = error.code;

    // Show ErrorModal for critical/blocking errors:

    // Network errors (no internet connection)
    if (!status && code === 'NETWORK_ERROR') return true;

    // Timeout errors
    if (code === 'TIMEOUT_ERROR') return true;

    // Server errors (500, 502, 503, etc.)
    if (status >= 500) return true;

    // Bad request (400)
    if (status === 400) return true;

    // Critical resource not found (404)
    if (status === 404) return true;

    // Permission denied for workflow operations (403)
    if (status === 403) return true;

    // Rate limiting (429) - user needs to wait
    if (status === 429) return true;

    // Don't show ErrorModal for these (use toast instead):
    // - 422 Unprocessable Entity (validation)
    // - Other 4xx client errors

    return false;
  }

  private shouldShowErrorNotification(error: any): boolean {
    const status = error.response?.status;

    // Don't show notifications for these cases:
    if (!status) return false; // Network errors will show ErrorModal instead

    // Don't show if ErrorModal will handle it
    if (status === 400) return false; // Bad request shows in ErrorModal
    if (status === 403) return false; // Forbidden shows in ErrorModal
    if (status === 404) return false; // Not found shows in ErrorModal
    if (status === 422) return false; // Unprocessable entity (validation errors)
    if (status === 429) return false; // Rate limit shows in ErrorModal
    if (status >= 500) return false; // Server errors show in ErrorModal

    // Show toast for these:
    if (status === 401) return true; // Unauthorized (after session modal)

    // Don't show for other 4xx errors by default
    return false;
  }

  // Public API methods
  private formatResponse<T>(response: AxiosResponse): ApiResponse<T> {
    // If response is already in ApiResponse format, return as is
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return response.data;
    }
    
    // Format axios response to ApiResponse
    return {
      success: response.status >= 200 && response.status < 300,
      data: response.data,
      timestamp: new Date().toISOString(),
      requestId: response.headers?.['x-request-id'] || Date.now().toString()
    };
  }

  public async get<T = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(url, { params });
      return this.formatResponse<T>(response);
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  public async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, data);
      return this.formatResponse<T>(response);
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  public async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put(url, data);
      return this.formatResponse<T>(response);
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete(url, config);
      return this.formatResponse<T>(response);
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Utility methods
  public getCsrfToken(): string | null {
    return this.getCsrfTokenFromCookie();
  }

  public getSessionToken(): string | null {
    return this.getSessionTokenFromCookie();
  }

  public setCsrfToken(token: string): void {
    this.setCookie('csrf_token', token, {
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      maxAge: 60 * 60 * 24 // 24 hours
    });
    console.log('🔒 CSRF token manually set');
  }

  public clearAuth(): void {
    this.clearCookie('csrf_token');
    this.clearCookie('session_token');
    console.log('🚫 Authentication cleared');
  }

  public clearAllAuthData(): void {
    // Clear cookies
    this.clearCookie('csrf_token');
    this.clearCookie('session_token');
    
    // Clear localStorage data
    const keysToRemove = [
      'access_token',
      'refresh_token', 
      'token_expiry',
      'user_data',
      'auth_email',
      'preferred_auth_method',
      'tenant_info',
      'guest_context',
      'booking_context'
    ];
    
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Cleared localStorage key: ${key}`);
      }
    });
    
    // Clear any sessionStorage as well
    sessionStorage.clear();
    
    console.log('🚫 All authentication and session data cleared');
  }

  private static validationInProgress: Promise<ApiResponse<{ valid: boolean; user?: any }>> | null = null;

  public async validateSession(sessionToken?: string): Promise<ApiResponse<{ valid: boolean; user?: any }>> {
    // Prevent concurrent validation requests
    if (ApiClient.validationInProgress) {
      console.log('🔄 Session validation already in progress, reusing existing request');
      return ApiClient.validationInProgress;
    }

    try {
      // Use provided session token or get from cookie
      const tokenToUse = sessionToken || this.getSessionToken();
      
      if (!tokenToUse) {
        console.log('🚫 No session token found in parameter or cookie');
        return {
          success: false,
          error: {
            code: 'NO_SESSION',
            message: 'No session token found'
          },
          timestamp: new Date().toISOString()
        };
      }

      console.log('🔍 Validating session with token:', tokenToUse.substring(0, 20) + '...');
      console.log('🌐 Making request to /validate-session endpoint...');
      
      // Set validation in progress and make API call
      ApiClient.validationInProgress = (async () => {
        try {
          const response = await this.client.post('/auth0/session/verify', {
            token: tokenToUse,
          });
          
          console.log('📡 Raw API response:', {
            status: response.status,
            data: response.data,
            headers: response.headers
          });
          
          return this.formatResponse(response);
        } finally {
          // Clear the in-progress flag
          ApiClient.validationInProgress = null;
        }
      })();
      
      return ApiClient.validationInProgress;
    } catch (error) {
      ApiClient.validationInProgress = null;
      console.error('❌ Session validation failed:', error);
      return {
        success: false,
        error: {
          code: 'VALIDATION_FAILED',
          message: 'Session validation failed'
        },
        timestamp: new Date().toISOString()
      };
    }
  }

}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;